import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import PageHeader from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { HandCoins, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';

const CHANNELS = [
  { id: 'ground',  label: 'Ground / door-to-door' },
  { id: 'social',  label: 'Social media (IG, TikTok, FB)' },
  { id: 'email',   label: 'Email / SMS list' },
  { id: 'referrals', label: 'Personal referrals' },
  { id: 'events',  label: 'Events / community' },
  { id: 'other',   label: 'Other' },
];

export default function AffiliateApplyPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '', experience: '', channels: [] });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const navigate = useNavigate();

  const toggleChannel = (id) => setForm(f => ({
    ...f,
    channels: f.channels.includes(id) ? f.channels.filter(c => c !== id) : [...f.channels, id],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Please provide your name and email');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/affiliate/apply', form);
      setDone(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Application failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Layout>
        <PageHeader eyebrow="Affiliate Program" title="Application received!" subtitle="We&apos;ll review your submission and email you within 24-48 hours." />
        <section className="max-w-2xl mx-auto px-4 py-10">
          <Card className="rounded-3xl border-[#E6E9EE]">
            <CardContent className="p-8 text-center">
              <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600" />
              <h2 className="font-headline text-2xl text-[#17352D] mt-4">You&apos;re in the queue, {form.name.split(' ')[0]}!</h2>
              <p className="text-slate-600 mt-2">{done.message}</p>
              <div className="mt-6 rounded-2xl bg-[#FBF7EE] border border-[#EED7A6] p-4 text-left">
                <div className="text-xs uppercase tracking-wider text-[#C8922F] font-semibold">Your affiliate code will look like</div>
                <div className="font-mono text-lg text-[#17352D] mt-1" data-testid="apply-code-preview">{done.affiliate_code_preview}</div>
                <p className="text-xs text-slate-500 mt-2">The final code is confirmed once your account is approved. You&apos;ll receive a login email with your personal share link.</p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                <Button variant="outline" onClick={() => navigate('/')} className="rounded-xl">Back to Home</Button>
                <Button onClick={() => navigate('/affiliate/leaderboard')} className="rounded-xl bg-[#17352D] text-white">See Leaderboard <ArrowRight className="h-4 w-4 ml-1" /></Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader eyebrow="Affiliate Program" title="Apply to become a Royal Palm affiliate" subtitle="Takes 2 minutes. You&apos;ll get a unique code + link once approved." />
      <section className="max-w-2xl mx-auto px-4 py-10" data-testid="affiliate-apply-page">
        <Card className="rounded-3xl border-[#E6E9EE]">
          <CardContent className="p-6 md:p-8">
            <form onSubmit={submit} className="space-y-4">
              <div className="flex items-center gap-2 text-[#C8922F] mb-2">
                <HandCoins className="h-5 w-5" />
                <span className="text-xs uppercase tracking-[0.22em] font-semibold">Your details</span>
              </div>
              <div>
                <Label htmlFor="a-name">Full name *</Label>
                <Input id="a-name" data-testid="apply-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jason Palm" className="h-11 rounded-xl mt-1" required />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="a-email">Email *</Label>
                  <Input id="a-email" type="email" data-testid="apply-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className="h-11 rounded-xl mt-1" required />
                </div>
                <div>
                  <Label htmlFor="a-phone">Phone</Label>
                  <Input id="a-phone" data-testid="apply-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="561-555-0123" className="h-11 rounded-xl mt-1" />
                </div>
              </div>
              <div>
                <Label htmlFor="a-city">City / area you cover</Label>
                <Input id="a-city" data-testid="apply-city" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="West Palm Beach, FL" className="h-11 rounded-xl mt-1" />
              </div>
              <div>
                <Label>Where will you sell?</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {CHANNELS.map(c => {
                    const active = form.channels.includes(c.id);
                    return (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => toggleChannel(c.id)}
                        data-testid={`apply-channel-${c.id}`}
                        className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors-only text-left ${active ? 'border-[#C8922F] bg-[#FBF7EE] text-[#17352D]' : 'border-[#E6E9EE] bg-white text-slate-600 hover:bg-slate-50'}`}
                      >
                        <Checkbox checked={active} className="pointer-events-none" />
                        <span>{c.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label htmlFor="a-exp">Tell us about your reach (optional)</Label>
                <Textarea id="a-exp" data-testid="apply-experience" rows={4} value={form.experience} onChange={e => setForm({ ...form, experience: e.target.value })} placeholder="e.g. 2k IG followers in South FL, community meetups on Sundays, canvassing crew of 3, past sales at HomeGoods…" className="rounded-xl mt-1" />
              </div>
              <Button type="submit" disabled={submitting} data-testid="apply-submit" className="w-full rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-11">
                {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <HandCoins className="h-4 w-4 mr-1" />}
                Submit Application
              </Button>
              <div className="text-[11px] text-slate-500 text-center">By applying you agree to our <Link to="/affiliate" className="underline">program terms</Link>. We&apos;ll never share your info.</div>
            </form>
          </CardContent>
        </Card>
      </section>
    </Layout>
  );
}
